import { HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { FormArray, FormBuilder, Validators } from "@angular/forms";
import { BehaviorSubject, combineLatest, concat, forkJoin, Observable, of, pipe, ReplaySubject, Subject, Subscription, throwError } from "rxjs";
import { catchError, filter, finalize, map, switchMap, take, tap } from "rxjs/operators";
import { CalendarEntry, NotificationTask, NotificationTaskType, ScheduleUserTask } from "src/app/classes";
import { MailService } from "src/app/lib/mail/services/mail.service";
import { CalendarService } from "src/app/lib/nextcloud/services/calendar.service";
import { FileService } from "src/app/lib/nextcloud/services/file.service";
import { DefaultMail, DownloadFilesTask, Issue, IssueTransaction, TransformIssueItemTask } from "../../../classes";
import { IssueTransactionMap, IssueTransactionRoles, IssueTransactionStatus } from "../../../globals";
import { IIssueProviderService, IIssueTransactionProviderService, ISSUE_PROVIDER_SERVICE, ISSUE_TRANSACTION_PROVIDER_SERVICE, ITransactionProvider } from "../../../interfaces";
import { IssueTransactionService } from "../../../services/issue-transaction.service";
import { IssueService } from "../../../services/issue.service";
import { NotificationPlaceholderReplacementService } from "../../../services/notification-placeholder-replacement.service";
import { IssueFormComponent } from "../../embedded/issue-form/issue-form.component";
import { TransactionSummaryFormComponent } from "../../embedded/transaction-summary-form/transaction-summary-form.component";
import { Step, StepStatus } from "./issue-transaction-dialog2-component-classes";

@Injectable()
export class IssueTransactionDialog2ComponentService implements ITransactionProvider {

    private _steps = new ReplaySubject<Step[]>();
    steps$ = this._steps.asObservable();
    noteStep$ = this._steps.pipe(map((x: Step[]) => x.find(x => x.role === IssueTransactionRoles.Note)));

    private _transactionStatus = new BehaviorSubject<number>(IssueTransactionStatus.Initial);
    transactionStatus$ = this._transactionStatus.asObservable();

    _subscriptions: Subscription[] = [];

    constructor(
        @Inject(ISSUE_TRANSACTION_PROVIDER_SERVICE) private _issueTransactionProviderService: IIssueTransactionProviderService,
        @Inject(ISSUE_PROVIDER_SERVICE) private _issueProviderService: IIssueProviderService,
        private _issueTransactionService: IssueTransactionService,
        private _issueService: IssueService,
        private _fileService: FileService,
        private _formBuilder: FormBuilder,
        private _mailService: MailService,
        private _calendarService: CalendarService,
        private _notificationPlaceholderReplacementService: NotificationPlaceholderReplacementService
    ) {
        this.setUp();
    }

    dispose() {
        this._steps.complete();
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
    }

    executeTransaction(): Observable<Object> {
        this._transactionStatus.next(IssueTransactionStatus.Started);
        return this.steps$.pipe(take(1), switchMap((steps: Step[]) => {
            let obs: Observable<Object>[] = [];
            for (let level of this.persistenceSequence) {
                for (let role of level) {
                    let levelSteps = steps.filter(x => x.role === role);
                    if (levelSteps.length > 0) obs.push(combineLatest(levelSteps.map(x => this.prepareStep(x))).pipe(take(1)));
                }
            }
            let subject = new Subject();
            concat(...obs).subscribe({
                next: () => { },
                error: () => this._transactionStatus.next(IssueTransactionStatus.Error),
                complete: () => {
                    combineLatest([this._issueProviderService.issue$, this._issueTransactionProviderService.issueTransaction$, this.noteStep$]).pipe(take(1))
                        .pipe(take(1), switchMap(([issue, issueTransaction, noteStep]) => this._issueTransactionService.markIssueTransactionAsExecuted(issue.issueid, issueTransaction.guid, noteStep ? noteStep.formGroup.controls['note'].value : null)))
                        .subscribe(() => {
                            subject.next();
                            subject.complete();
                            this._transactionStatus.next(IssueTransactionStatus.Succeeded);
                        })
                }
            });
            return subject.asObservable();
        }));
    }

    getDependendSteps(step: Step): Observable<Step[]> {
        return this.steps$.pipe(take(1), map((steps: Step[]) => {
            let level = this.persistenceSequence.findIndex(x => x.indexOf(step.role) > -1);
            if (level === -1) return [];
            return steps.filter(x => this.persistenceSequence.findIndex(y => y.indexOf(x.role) > -1) > level);
        }));
    }

    markDependendStepsAsFailed(step: Step){
        this.getDependendSteps(step).subscribe((steps: Step[]) => {
            steps.forEach(x => x.status.next(StepStatus.Failed));
            steps.forEach(x => {
                if(Array.isArray(x.errors)) x.errors.push({ status: null, error: 'Voraussetzung ist fehlgeschlagen' } as HttpErrorResponse);
            });
        });
    }

    prepareStep(step: Step): Observable<Object> {

        let errorHandler = pipe(
            catchError((error) => {
                this.markDependendStepsAsFailed(step);
                step.status.next(StepStatus.Failed);
                step.errors.push(error);
                return throwError(error);
            })
        );

        var obs: Observable<Object[]>;
        step.status.next(StepStatus.Started);

        switch (step.role) {

            case IssueTransactionRoles.BookIssueItems:
                let tasks = (step.formGroup.controls['tasks'].value as TransformIssueItemTask[]).filter(x => x && x.contract);
                obs = tasks.length === 0 ? of(null) : combineLatest(tasks.map(x => this._issueService.bookIssueItem(x)));
                return obs.pipe(
                    tap(() => step.status.next(StepStatus.Succeeded)),
                    errorHandler
                );

            case IssueTransactionRoles.CreateIssue:
                return this.noteStep$.pipe(take(1), switchMap((x: Step) => this._issueService.createIssue(step.formGroup.value, x ? x.formGroup.controls['note'].value : null).pipe(
                    tap((result: Issue) => {
                        this._issueProviderService.updateIssue(result);
                        step.status.next(StepStatus.Succeeded);
                    }),
                    errorHandler
                )));

            case IssueTransactionRoles.EditIssue:
                return this.noteStep$.pipe(switchMap((x: Step) => this._issueService.updateIssue(step.formGroup.value, x ? x.formGroup.controls['note'].value : null).pipe(
                    tap((result: Issue) => {
                        this._issueProviderService.updateIssue(result);
                        step.status.next(StepStatus.Succeeded);
                    }),
                    errorHandler
                )));

            case IssueTransactionRoles.FolderStructure:
                let uris = (step.formGroup.controls['download'].value as string[]);
                obs = uris.length === 0 ? of(null) : (this._fileService.filesToZip({ download: uris } as DownloadFilesTask).pipe(tap((result) => {
                    const url = window.URL.createObjectURL(result);
                    window.open(url);
                })));
                return obs.pipe(
                    tap(() => step.status.next(StepStatus.Succeeded)),
                    errorHandler
                );

            case IssueTransactionRoles.User:
                let userTasks = (step.formGroup.controls['users'].value as ScheduleUserTask[]);
                obs = userTasks.length === 0 ? of(null) : this._issueProviderService.issueId$.pipe(switchMap((issueId: number) => forkJoin(userTasks.map(x => this._issueService[x.schedule ? 'assignUser' : 'unassignUser'](issueId, x.user.id)))));
                return obs.pipe(
                    tap(() => step.status.next(StepStatus.Succeeded)),
                    errorHandler
                );

            case IssueTransactionRoles.Notification:
                let notificationTasks = (step.formGroup.controls['notifications'].value as NotificationTask[]);
                if (notificationTasks.length === 0) return of(null);
                obs = this._issueProviderService.issue$.pipe(take(1), switchMap((x: Issue) => combineLatest(notificationTasks.map(y => {
                    return this._notificationPlaceholderReplacementService.replace(y, x).pipe(switchMap(_ => {
                        if (y.type === NotificationTaskType.EMail) {
                            y.issueId = x.issueid;
                            return this._mailService.sendMail(y, y.receiver).pipe(errorHandler);
                        } else return this._mailService.sendInternalNotification(y, y.receiver[0]);
                    }))
                }))));
                return obs.pipe(
                    finalize(() => {
                        if (step.status.value !== StepStatus.Failed) step.status.next(StepStatus.Succeeded);
                    }),
                    errorHandler
                );

            case IssueTransactionRoles.Upload:
                var counter = 0;
                (step.formGroup.controls['upload'].value.data as FormData).forEach(() => counter++);
                if (counter === 0) return of(null);
                obs = this._issueProviderService.issue$.pipe(filter(x => x ? true : false), take(1), switchMap((issue: Issue) => this._fileService.uploadFile(issue.folderlocation, step.formGroup.controls['upload'].value.data)));
                return obs.pipe(
                    tap(() => step.status.next(StepStatus.Succeeded)),
                    errorHandler
                );

            case IssueTransactionRoles.MakeAppointment:
                let appointments = step.formGroup.controls['appointments'].value as CalendarEntry[];
                if (appointments.length === 0) return of(null);
                obs = combineLatest(appointments.map(x => this._calendarService.createEvent(x)));
                return obs.pipe(
                    tap(() => step.status.next(StepStatus.Succeeded)),
                    errorHandler
                );

        }

        return of(step);
    }

    setUp() {
        this._subscriptions.push(...[
            combineLatest([this._issueTransactionProviderService.issueTransaction$, this._issueProviderService.issue$, this._issueTransactionProviderService.issueTransactionDefaultMails$, this._issueTransactionProviderService.issueTransactionAttributes$])
                .pipe(take(1))
                .subscribe(([issueTransaction, issue, defaultMails, attributes]) => {
                    let step = {
                        title: () => of('Kopfdaten bearbeiten'),
                        formGroup: this._formBuilder.group(new Issue()),
                        view: IssueFormComponent,
                        summaryView: null,
                        isHidden: true,
                        role: issue && issue.issueid ? IssueTransactionRoles.EditIssue : IssueTransactionRoles.CreateIssue,
                        status: new BehaviorSubject(StepStatus.Pending),
                        visibleInConclusion: false
                    } as Step;
                    if (issue) step.formGroup.patchValue(issue);
                    else this._issueProviderService.getCurrentIssueType().subscribe((type) => step.formGroup.controls['issuetype'].setValue(type));
                    let steps = [step];
                    for (let attr of attributes) {
                        
                        if (attr === 1) {
                            step.isHidden = false;
                            step.visibleInConclusion = true;
                        }
                        else {
                            let view = IssueTransactionMap[attr];
                            steps.push({
                                title: () => of(view.name),
                                formGroup: view.formObject(this._formBuilder),
                                view: view.view,
                                isHidden: false,
                                summaryView: view.summaryView,
                                role: view.role,
                                status: new BehaviorSubject(StepStatus.Pending),
                                visibleInConclusion: view.visibleInConclusion,
                                errors: []
                            } as Step);
                        }
                        
                    }
                    steps.push({
                        title: () => of('Zusammenfassung'),
                        formGroup: this._formBuilder.group({}),
                        view: TransactionSummaryFormComponent,
                        isHidden: false,
                        summaryView: null,
                        role: null,
                        status: new BehaviorSubject(StepStatus.NoAction),
                        visibleInConclusion: false
                    } as Step);
                    this.patchValues(steps, issueTransaction, issue, defaultMails);
                    this._steps.next(steps);
                })
        ]);
    }

    patchValues(steps: Step[], issueTransaction: IssueTransaction, issue: Issue, defaultMails: DefaultMail[] = []) {
        if (typeof issueTransaction.result === 'number') steps.find(x => x.role === IssueTransactionRoles.CreateIssue || x.role === IssueTransactionRoles.EditIssue).formGroup.controls['issuestate'].setValue(issueTransaction.result);
        steps.filter(x => x.role === IssueTransactionRoles.Notification).forEach(x => {
            defaultMails.forEach(y => {
                (x.formGroup.controls['notifications'] as FormArray).push(this._formBuilder.group({
                    attachements: this._formBuilder.array([]),
                    isDefaultNotification: this._formBuilder.control(true),
                    content: this._formBuilder.control(null),
                    subject: this._formBuilder.control(null, Validators.required),
                    receiver: this._formBuilder.control(y.mailreceiver, Validators.compose([Validators.required, Validators.email])),
                    type: this._formBuilder.control(NotificationTaskType.EMail),
                    defaultNotification: this._formBuilder.control(y.defaultnotification)
                }));
            });
        });
        steps.filter(x => x.role === IssueTransactionRoles.Conclusion).forEach(x => x.formGroup.controls['url'].setValue(`api/report-server/issue-conclusion/${issue.issueid}`));
        steps.filter(x => x.role === IssueTransactionRoles.OperationProtocol).forEach(x => x.formGroup.controls['url'].setValue(`api/report-server/operation-protocol/${issue.issueid}`));
    }

    persistenceSequence = [
        [IssueTransactionRoles.CreateIssue],
        [IssueTransactionRoles.EditIssue],
        [IssueTransactionRoles.FolderStructure, IssueTransactionRoles.User, IssueTransactionRoles.Notification, IssueTransactionRoles.Upload, IssueTransactionRoles.BookIssueItems, IssueTransactionRoles.MakeAppointment]
    ];

}
